import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import "../global.scss";
import CardComp from '../components/card';
import { message } from 'antd';
import { useAtom } from 'jotai';
import { userAtom, papersAtom } from '../atom';
import { useDataFetching } from '../components/helperfunction';

const ITEMS_PER_PAGE = 12;
const SKELETON_COUNT = 6;

function matchesQuery(title, query) {
  return (title || '').toLowerCase().includes(query);
}

export default function Dashboard({ isAdmin }) {
  const { getData } = useDataFetching();
  const [, setPapersData] = useAtom(papersAtom);
  const user = useAtom(userAtom)?.[0];
  const [userSavedData, setUserSavedData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [constData, setConstData] = useState([]);
  const [buttonState, setButtonDisabled] = useState(false);
  const [tab, setTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const hasFetchedRef = useRef(false);
  const searchRef = useRef(null);

  const getPapers = useCallback(async () => {
    setLoading(true);
    const allData = await getData();
    setData(allData);
    setConstData(allData);
    setLoading(false);
  }, [getData]);

  useEffect(() => {
    setPapersData(data);
  }, [data, setPapersData]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    searchRef.current?.focus();
  };

  const sendRequestEmail = async (e) => {
    e.preventDefault();
    const subject = searchQuery.trim();
    if (!subject) return;
    setButtonDisabled(true);
    try {
      const response = await fetch(process.env.REACT_APP_SUBJECT_LINK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: user?.email || 'anonymous@gmail.com',
          subject_name: subject,
        }),
      });
      if (response.ok) {
        message.success(`Requested “${subject}”`);
      } else {
        message.warning('Could not send the request. Try again.');
      }
    } catch (error) {
      message.error('Could not send the request. Try again.');
    }
    setButtonDisabled(false);
  };

  useEffect(() => {
    const filtered = constData.filter(paper =>
      user?.data?.saved?.includes(paper?.data?.id)
    );
    setUserSavedData(filtered);
  }, [constData, user]);

  useEffect(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? constData.filter(item => matchesQuery(item?.data?.title, query))
      : constData;
    setData(filtered);
  }, [searchQuery, constData]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    document.title = 'Subjects — SitsCom';
    getPapers();
  }, [getPapers]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!user && tab === 2) setTab(1);
  }, [user, tab]);

  const currentData = tab === 1 ? data : userSavedData.filter(item =>
    matchesQuery(item?.data?.title, searchQuery.toLowerCase().trim())
  );
  const visibleCount = currentData.length;
  const totalPages = Math.ceil(visibleCount / ITEMS_PER_PAGE) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return currentData.slice(start, start + ITEMS_PER_PAGE);
  }, [currentData, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [tab]);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  }, [currentPage, tab]);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const showSavedEmpty = tab === 2 && user && !loading && visibleCount === 0 && !searchQuery.trim();
  const showNoResults = tab === 1 && !loading && visibleCount === 0;
  const showSavedNoMatch = tab === 2 && user && !loading && visibleCount === 0 && searchQuery.trim();
  const showGrid = loading || paginatedData.length > 0;

  const countLabel = (() => {
    if (loading) return 'Loading subjects';
    const noun = visibleCount === 1 ? 'subject' : 'subjects';
    const q = searchQuery.trim();
    if (tab === 2 && q) return `${visibleCount} saved match “${q}”`;
    if (tab === 2) return `${visibleCount} saved`;
    if (q) return `${visibleCount} match “${q}”`;
    return `${visibleCount} ${noun}`;
  })();

  return (
    <div className="dashboard page-container">
      <header className="dashboard-header">
        <span className="dash-pin" aria-hidden="true" />
        <h1>Subjects</h1>
        <p className="dashboard-count" aria-live="polite">{countLabel}</p>
      </header>

      <div className="dashboard-search">
        <label htmlFor="subject-search" className="visually-hidden">Search subjects</label>
        <span className="search-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </span>
        <input
          id="subject-search"
          ref={searchRef}
          type="search"
          value={searchQuery}
          placeholder="OS, PF, Applied Physics"
          autoComplete="off"
          enterKeyHint="search"
          onChange={handleSearchChange}
        />
        {searchQuery && (
          <button type="button" className="search-clear" onClick={clearSearch}>
            Clear
          </button>
        )}
      </div>

      {user && (
        <div className="dashboard-tabs" role="tablist" aria-label="Subject lists">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 1}
            className={tab === 1 ? 'active' : ''}
            onClick={() => setTab(1)}
          >
            All subjects
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 2}
            className={tab === 2 ? 'active' : ''}
            onClick={() => setTab(2)}
          >
            Saved
          </button>
        </div>
      )}

      {showSavedEmpty && (
        <div className="dashboard-empty">
          <p className="empty-title">No saved subjects</p>
          <p>Open a subject and save it to find it here.</p>
        </div>
      )}

      {showSavedNoMatch && (
        <div className="dashboard-empty">
          <p className="empty-title">No saved subjects match “{searchQuery.trim()}”</p>
          <p>Clear search to see everything you saved.</p>
          <button type="button" className="btn btn-secondary" onClick={clearSearch}>
            Clear search
          </button>
        </div>
      )}

      {showGrid && (
        <div className="dashboard-grid stagger-children" key={`page-${currentPage}-tab-${tab}`}>
          {loading
            ? Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <div className="skeleton-card" key={i}>
                  <div className="skel-line skel-title skeleton" />
                  <div className="skel-line skel-desc1 skeleton" />
                </div>
              ))
            : paginatedData.map((item, i) => (
                <CardComp key={item?.data?.id || i} isAdmin={isAdmin} data={item} loading={false} />
              ))
          }
        </div>
      )}

      {!loading && visibleCount > ITEMS_PER_PAGE && (
        <nav className="dashboard-pagination fade-in" aria-label="Subject pages">
          <button
            type="button"
            disabled={currentPage === 1}
            aria-label="Previous page"
            onClick={() => setCurrentPage(p => p - 1)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          {getPageNumbers().map((page, i) =>
            page === '...' ? (
              <span key={`dots-${i}`} className="pagination-dots" aria-hidden="true">...</span>
            ) : (
              <button
                type="button"
                key={page}
                className={currentPage === page ? 'active-page' : ''}
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            )
          )}

          <button
            type="button"
            disabled={currentPage === totalPages}
            aria-label="Next page"
            onClick={() => setCurrentPage(p => p + 1)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </nav>
      )}

      {showNoResults && (
        <div className="dashboard-empty">
          <p className="empty-title">
            {searchQuery.trim()
              ? `No subjects match “${searchQuery.trim()}”`
              : 'No subjects yet'}
          </p>
          <p>
            {searchQuery.trim()
              ? 'Request it and we will add it when we can.'
              : 'Check back soon.'}
          </p>
          {searchQuery.trim() && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={sendRequestEmail}
              disabled={buttonState}
            >
              {buttonState ? 'Sending…' : `Request “${searchQuery.trim()}”`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
